import { useState } from "react"
import { useOutletContext } from "react-router-dom"
import ToolShell from "../components/ToolShell"
import CTABox from "../components/CTABox"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

export default function MaintenanceBudgetEstimatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [propertyValue, setPropertyValue] = useState(250000)
  const [squareFootage, setSquareFootage] = useState(1200)
  const [monthlyRent, setMonthlyRent] = useState(1500)
  
  const [age, setAge] = useState<"<10" | "10-30" | "30-50" | "50+">("10-30")
  const [propertyType, setPropertyType] = useState<"Apartment" | "House" | "Multi-family" | "Commercial">("House")
  const [units, setUnits] = useState(2)

  // Calcs
  const AGE_MULTIPLIER = { "<10": 0.75, "10-30": 1.0, "30-50": 1.5, "50+": 2.0 }
  
  const method1 = propertyValue * 0.01
  const method2 = squareFootage * 1 // assuming $1/sqft for simplicity
  const method3 = monthlyRent * 12 * 0.15 // 50% rule derived (15% CapEx/maint)
  
  const multiplier = AGE_MULTIPLIER[age]
  const avgBudget = ((method1 + method2 + method3) / 3) * multiplier
  
  const isMultiFamily = propertyType === "Multi-family"
  const finalBudget = isMultiFamily ? avgBudget * units * 0.85 : avgBudget
  const monthlyReserve = finalBudget / 12

  // Category breakdown
  const categories = [
    { label: "HVAC / Boiler", pct: 30 },
    { label: "Plumbing / Pipes", pct: 20 },
    { label: "Roof / Exterior", pct: 15 },
    { label: "Appliances", pct: 15 },
    { label: "Other / Cosmetic", pct: 20 }
  ]

  return (
    <ToolShell>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 p-6 sm:p-8">
        
        {/* Input Column */}
        <div className="lg:col-span-1 space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Property Value</label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
              <input type="number" value={propertyValue || ''} onChange={(e) => setPropertyValue(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Square Footage</label>
            <input type="number" value={squareFootage || ''} onChange={(e) => setSquareFootage(Number(e.target.value))} className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Rent</label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
              <input type="number" value={monthlyRent || ''} onChange={(e) => setMonthlyRent(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Property Age</label>
            <select value={age} onChange={(e) => setAge(e.target.value as any)} className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary bg-white">
              <option value="<10">Under 10 years</option>
              <option value="10-30">10 – 30 years</option>
              <option value="30-50">30 – 50 years</option>
              <option value="50+">50+ years</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Property Type</label>
            <select value={propertyType} onChange={(e) => setPropertyType(e.target.value as any)} className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary bg-white">
              <option value="Apartment">Apartment / Flat</option>
              <option value="House">Single Family House</option>
              <option value="Multi-family">Multi-family / HMO</option>
              <option value="Commercial">Commercial Shop</option>
            </select>
          </div>

          {isMultiFamily && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Number of Units</label>
              <input type="number" value={units || ''} onChange={(e) => setUnits(Number(e.target.value))} className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary" />
            </div>
          )}
        </div>

        {/* Results Column */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 p-6 sm:p-8 rounded-2xl flex flex-col items-center text-center">
            <span className="text-sm font-semibold text-primary uppercase tracking-wider mb-2">Recommended Annual Budget</span>
            <span className="text-5xl font-extrabold text-slate-900 tracking-tight mb-2">{currency}{finalBudget.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            <span className="text-slate-600 font-medium">Save {currency}{monthlyReserve.toLocaleString(undefined, { maximumFractionDigits: 0 })} every month</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-white border border-slate-200 rounded-xl text-center">
              <div className="text-xs text-slate-500 mb-1 uppercase tracking-wider font-medium">1% Rule</div>
              <div className="text-xl font-bold text-slate-800">{currency}{method1.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            </div>
            <div className="p-4 bg-white border border-slate-200 rounded-xl text-center">
              <div className="text-xs text-slate-500 mb-1 uppercase tracking-wider font-medium">Sq Ft Rule</div>
              <div className="text-xl font-bold text-slate-800">{currency}{method2.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            </div>
            <div className="p-4 bg-white border border-slate-200 rounded-xl text-center">
              <div className="text-xs text-slate-500 mb-1 uppercase tracking-wider font-medium">50% Rule (Derived)</div>
              <div className="text-xl font-bold text-slate-800">{currency}{method3.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h4 className="font-semibold text-slate-900 mb-4">Estimated Category Breakdown</h4>
            <div className="space-y-3">
              {categories.map((c, i) => {
                const amt = finalBudget * (c.pct / 100)
                return (
                  <div key={i} className="flex justify-between items-center text-sm border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                    <span className="text-slate-600">{c.label} ({c.pct}%)</span>
                    <span className="font-medium text-slate-900">{currency}{amt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 p-6 rounded-xl">
            <h4 className="font-semibold text-slate-900 mb-4">5-Year CapEx Forecast Timeline</h4>
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="text-slate-500 border-b border-slate-200">
                  <th className="pb-2 font-medium">System</th>
                  <th className="pb-2 font-medium">Lifespan</th>
                  <th className="pb-2 font-medium text-right">Replacement Est.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/50">
                <tr><td className="py-2.5 text-slate-800">Boiler/HVAC</td><td className="py-2.5 text-slate-500">10-15 years</td><td className="py-2.5 text-right font-medium">{currency}3k–{currency}8k</td></tr>
                <tr><td className="py-2.5 text-slate-800">Roof</td><td className="py-2.5 text-slate-500">15-30 years</td><td className="py-2.5 text-right font-medium">{currency}5k–{currency}15k</td></tr>
                <tr><td className="py-2.5 text-slate-800">Kitchen Refit</td><td className="py-2.5 text-slate-500">15-20 years</td><td className="py-2.5 text-right font-medium">{currency}8k–{currency}20k</td></tr>
                <tr><td className="py-2.5 text-slate-800">Windows (uPVC)</td><td className="py-2.5 text-slate-500">20-30 years</td><td className="py-2.5 text-right font-medium">{currency}5k–{currency}12k</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="px-6 sm:px-8 pb-10">
        <FormulaBox 
          formula={`Method 1 (1% Rule) = Property Value × 0.01\nMethod 2 (SqFt Rule) = Square Footage × 1.00\nMethod 3 (Rent Derived) = Annual Rent × 0.15\n\nBase Budget = Average of Methods 1, 2, 3\nAge Multiplier = <10yrs (0.75x) | 10-30yrs (1.0x) | 30-50yrs (1.5x) | 50+yrs (2.0x)\n\nFinal Annual Budget = Base Budget × Age Multiplier`} 
        />
        
        <CTABox 
          icon={tool.icon} 
          heading="Never get hit by a surprise bill again" 
          body={tool.ctaText} 
          ctaLabel="Set up RentKeep reserves" 
          ctaHref="/register" 
        />
        
        <ToolFAQ 
          items={[
            { q: "What is CapEx vs Operating Maintenance?", a: "Operating maintenance covers small immediate fixes (leaky taps, changing locks). CapEx (Capital Expenditures) encompasses major replacements like roof, boiler, or structural upgrades. This budget aims to cover both via long-term sinking funds." },
            { q: "Why does the 1% rule vary so much by condition?", a: "A newly built property under warranty might need less than 0.5% a year. A 100-year-old historic home could easily eat up 2% or 3% annually in structural upkeep and efficiency upgrades." }
          ]} 
        />
        
        <RelatedToolsList relatedSlugs={tool.related} />
      </div>
    </ToolShell>
  )
}
